import { CartApi } from '@/api/cart-api'

import { defineStore } from 'pinia'

import {
  STATUS_LOAD,
  CART_NUM_ITEMS_PER_PAGE,
  KEY_USERS,
  KEY_STATUS,
  KEY_ZIP,
  KEY_ITEM,
  MAX_HOURS_SIGN_IN,
} from '@/constants'

import { useCommonStore } from '@/stores/common'

import { parse, differenceInHours } from 'date-fns'

interface LastCart {
  id: number
  timestamp_created: string
  photos: Array<any>
}

interface State {
  items: Array<any>
  carts: Array<any>
  offset: number
  limit: number
  lastCart: LastCart
  axiosCurrentRequestId: string
  api: CartApi
  status: STATUS_LOAD
  zipFile: string
  morePreviousAvailable: boolean
  moreNextAvailable: boolean
  filter: number
  filterDates: Record<string, string>
  filterStatus: Array<string>
  filterUsers: Array<string>
  filterOptionValues: Record<string, any>
  filterOptionsSelected: Array<Record<string, string>>
  areAdminResultAggregate: boolean
}

export const useCartStore = defineStore('cart', {
  persist: {
    afterRestore: (context) => {
      console.log('userCartStore afterRestore: ', context)

      const signedIngDate: any = useCommonStore().getSignedInDate
      console.log('Cart Store afterRestore', signedIngDate)

      if (signedIngDate !== '') {
        const signedInDateConverted = parse(
          signedIngDate,
          "yyyy-MM-dd'T'HH:mm:ss.SSSX",
          new Date()
        )

        const currentDate: Date = new Date()

        console.log(
          'Cart Store afterRestore signedInDateConverted',
          signedInDateConverted
        )

        const hoursSinceSignedIn = differenceInHours(
          currentDate,
          signedInDateConverted
        )

        console.log('Cart Store afterRestore', hoursSinceSignedIn)

        // Check if the signedIn is more than 24h ago, if it is, we automatically sign out
        if (hoursSinceSignedIn >= MAX_HOURS_SIGN_IN) {
          context.store.reset()
        }
      } else {
        context.store.reset()
      }
    },
  },
  // convert to a function
  state: (): State => ({
    items: [],
    carts: [],
    offset: 0,
    limit: CART_NUM_ITEMS_PER_PAGE,
    lastCart: {
      id: -1,
      timestamp_created: '',
      photos: [],
    },
    axiosCurrentRequestId: '',
    api: new CartApi(),
    status: STATUS_LOAD.IDLE,
    zipFile: '',
    morePreviousAvailable: false,
    moreNextAvailable: false,
    filter: 0,
    filterDates: {},
    filterStatus: [],
    filterUsers: [],
    filterOptionValues: {},
    filterOptionsSelected: [],
    areAdminResultAggregate: true,
  }),

  getters: {
    getItems: (state) => {
      return state.items
    },

    getCarts: (state) => {
      return state.carts
    },

    getLastCart: (state) => {
      console.log('getLastCart', state.lastCart)
      return state.lastCart
    },

    getStatus: (state) => {
      return state.status
    },

    getFilterOrder: (state) => {
      return state.filter
    },

    getNumItems: (state) => {
      return state.items.length
    },

    isMorePreviousAvailable: (state) => {
      return state.morePreviousAvailable
    },

    isMoreNextAvailable: (state) => {
      return state.moreNextAvailable
    },

    getFilterOptionUsers: (state) => {
      return KEY_USERS in state.filterOptionValues
        ? state.filterOptionValues[KEY_USERS]
        : []
    },

    getFilterOptionsSelected: (state) => {
      return state.filterOptionsSelected
    },

    getFilterStatusesSelected: (state) => {
      return state.filterStatus
    },

    getZipFile: (state) => {
      return state.zipFile
    },

    getAreAdminResultsAggregate: (state) => {
      return state.areAdminResultAggregate
    },

    isInCart: (state) => {
      return (id: number) =>
        state.items.find((item) => item.id === id) !== undefined
    },
  },

  actions: {
    setFilter(filter: number): void {
      console.log('setFilter')

      this.filter = filter

      this.pull()
    },

    addFilterStatuses(status: string, optionText: string) {
      this.filterStatus.push(status)
      this.filterOptionsSelected.push({
        type: 'status',
        value: status,
        text: optionText,
      })

      // Automatically refresh the list of carts
      this.pull()
    },

    removeFilterStatus(status: string, optionText: string) {
      this.filterStatus = this.filterStatus.filter((item) => item !== status)
      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.text !== optionText
      )

      // Automatically refresh the list of carts
      this.pull()
    },

    addFilterUser(user: string): void {
      if (!this.filterUsers.includes(user)) {
        this.filterUsers.push(user)
        this.filterOptionsSelected.push({ type: 'user', text: user })

        // Automatically refresh the list of carts
        this.pull()
      }
    },

    removeFilterUser(user: string): void {
      this.filterUsers = this.filterUsers.filter((item) => item !== user)
      this.filterOptionsSelected = this.filterOptionsSelected.filter(
        (item) => item.text !== user
      )

      // Automatically refresh the list of carts
      this.pull()
    },

    removeFilterOptionsSelected(filter: Record<string, string>): void {
      console.log('removeFilterOptionsSelected', filter)

      if (filter.type === 'user') {
        this.removeFilterUser(filter.text)
      } else {
        this.removeFilterStatus(filter.value, filter.text)
      }
    },

    setFilterDates(dates: Record<string, string>) {
      this.filterDates = dates

      // Automatically refresh the list of carts
      this.pull()
    },

    setAreAdminResultsAggregate(value: boolean) {
      console.log('setAreAdminResultsAggregate', value)

      this.areAdminResultAggregate = value

      // this.pull()
    },

    // no context as first argument, use `this` instead
    async purchase(userEmail: string) {
      console.log('purchase')

      try {
        this.status = STATUS_LOAD.LOADING

        const payload: Record<
          string,
          Array<Record<string, string | number>>
        > = {
          photo_ids: this.items.map((item) => {
            return {
              id: item.id,
              s3_key: item.s3_key,
            }
          }),
        }

        const response: any = await this.api.purchase(userEmail, payload)
        console.log('data', response)

        this.zipFile = response.data[KEY_ZIP]
        this.lastCart = response.data[KEY_ITEM]

        this.items = []

        this.status = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.status = STATUS_LOAD.FAILED
      } finally {
        this.status = STATUS_LOAD.IDLE
      }
    },

    async pullLastCart(user_email: string) {
      console.log('pullLastCart')

      try {
        this.lastCart.id = -1
        this.lastCart.timestamp_created = ''
        this.lastCart.photos = []

        const response: any = await this.api.pullLastCart(user_email)
        console.log('data', response)

        this.lastCart = response.data
      } catch (error) {
        console.error(error)
      }
    },

    async pull() {
      console.log('pull')

      try {
        this.status = STATUS_LOAD.LOADING

        this.offset = 0
        this.carts = []

        const payload = this.getCartPayload(this.offset)

        const response: any = await this.api.pull(payload)
        console.log('pull data', response)

        this.moreNextAvailable = response.data.has_more_items

        if (response.data.items.length > 0) {
          this.carts = this.getCartDataFormatted(response.data.items)
        }

        this.morePreviousAvailable = false

        this.status = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.status = STATUS_LOAD.FAILED
      }
    },

    async pullPrevious() {
      console.log('pullPrevious')

      try {
        this.status = STATUS_LOAD.LOADING

        const payload = this.getCartPayload(this.offset - this.limit)

        const response: any = await this.api.pull(payload)
        console.log('pull data', response)

        this.carts = this.getCartDataFormatted(response.data.items)

        this.offset = this.offset - this.limit

        if (this.offset === 0) {
          this.morePreviousAvailable = false
        }
        this.moreNextAvailable = true

        this.status = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.status = STATUS_LOAD.FAILED
      }
    },

    async pullNext() {
      console.log('pullNext')

      try {
        this.status = STATUS_LOAD.LOADING

        const payload = this.getCartPayload(this.offset + this.limit)

        const response: any = await this.api.pull(payload)
        console.log('pullNext', response)

        this.moreNextAvailable = response.data.has_more_items

        if (response.data.items.length > 0) {
          this.carts = this.getCartDataFormatted(response.data.items)

          this.offset = this.offset + this.limit

          this.morePreviousAvailable = true
        }

        this.status = STATUS_LOAD.LOADED
      } catch (error) {
        console.error(error)

        this.status = STATUS_LOAD.FAILED
      }
    },

    getCartPayload(
      offset: number
    ): Record<string, number | string | Array<string>> {
      console.log('getCartPayload')

      let payload: Record<string, number | string | Array<string>> = {
        offset: offset,
        limit: this.limit,
        order: this.filter,
      }

      console.log('getCartPayload payload', payload)

      if (this.filterStatus.length > 0) {
        payload[KEY_STATUS] = this.filterStatus
      }

      if (this.filterUsers.length > 0) {
        payload[KEY_USERS] = this.filterUsers
      }

      payload['query_type'] = this.areAdminResultAggregate
        ? 'aggregate'
        : 'per_purchase'

      payload = { ...payload, ...this.filterDates }

      return payload
    },

    getCartDataFormatted(
      rawData: Array<Record<string, any>>
    ): Array<Record<string, any>> {
      console.log('cartsFormatted rawData', rawData)

      const cartsFormatted: Array<Record<string, any>> = []
      rawData.forEach((element: Record<string, any>) => {
        cartsFormatted.push(element)
        // Add an empty object for allowing us to show the list of photos linked to it in the table list
        cartsFormatted.push({})
      })

      console.log('cartsFormatted', cartsFormatted)

      return cartsFormatted
    },

    deleteFromCart(id: number): void {
      console.log('deleteFromCart')

      this.items = this.items.filter((item) => item.id !== id)

      if (this.lastCart) {
        const item: Record<string, any> | undefined =
          this.lastCart!.photos.find((item) => item.id === id)
        if (item) {
          item.is_in_cart = false
        }
      }
    },

    putInCart(item: Record<string, any>): void {
      console.log('putInCart')

      this.items.push(item)
    },

    putBackInCart(id: number): void {
      console.log('putBackInCart', id, this.lastCart)

      const item: Record<string, any> = this.lastCart!.photos.find(
        (item) => item.id === id
      )
      item.is_in_cart = true

      this.items.push(item)
    },

    deleteAllFromCart(): void {
      console.log('deleteAllFromCart')

      this.items = []
    },

    // easily reset state using `$reset`
    reset() {
      this.items = []
      this.carts = []
      this.offset = 0
      this.limit = CART_NUM_ITEMS_PER_PAGE
      ;(this.lastCart = {
        id: -1,
        timestamp_created: '',
        photos: [],
      }),
        (this.axiosCurrentRequestId = '')
      this.status = STATUS_LOAD.IDLE
      this.zipFile = ''
      this.morePreviousAvailable = false
      this.moreNextAvailable = false
      this.filter = 0
      this.filterDates = {}
      this.filterStatus = []
      this.filterUsers = []
      this.filterOptionValues = {}
      this.filterOptionsSelected = []
    },
  },
})
